import React, { useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ArrowRight } from "lucide-react";
import { getCaseStudy, CASE_STUDIES, smSrc } from "../data/caseStudies";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";

const Block = ({ label, children }) => (
  <Reveal>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 border-t border-charcoal/10">
      <p className="md:col-span-3 text-xs tracking-[0.25em] uppercase font-semibold text-gold">{label}</p>
      <p className="md:col-span-9 text-lg md:text-xl leading-relaxed text-charcoal/80 whitespace-pre-line">{children}</p>
    </div>
  </Reveal>
);

export default function CaseStudy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const study = getCaseStudy(id);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!study) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-cream" data-testid="case-not-found">
        <h1 className="font-heading text-4xl text-charcoal">Case study not found</h1>
        <Link to="/" className="text-burgundy underline">Back home</Link>
      </div>
    );
  }

  if (study.external) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-cream px-6 text-center" data-testid="case-external">
        <h1 className="font-heading text-4xl text-charcoal">{study.title}</h1>
        <p className="text-charcoal/60 max-w-md">{study.summary}</p>
        <a href={study.external} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-charcoal text-cream hover:bg-burgundy px-8 py-4 text-sm font-medium transition-colors">
          View on Behance <ArrowUpRight size={18} />
        </a>
        <Link to="/" className="text-burgundy underline text-sm">Back home</Link>
      </div>
    );
  }

  const internal = CASE_STUDIES.filter((c) => !c.external);
  const iIdx = internal.findIndex((c) => c.id === id);
  const next = internal[(iIdx + 1) % internal.length];

  return (
    <div className="bg-cream" data-testid="case-study-page">
      {/* Back nav */}
      <div className="fixed top-0 left-0 right-0 z-[999] backdrop-blur-xl bg-cream/70 border-b border-charcoal/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-charcoal hover:text-burgundy transition-colors" data-testid="case-back-button">
            <ArrowLeft size={18} /> Back to work
          </button>
          <span className="font-heading text-lg tracking-tight text-charcoal">Ankita Gogia<span className="text-burgundy">.</span></span>
        </div>
      </div>

      {/* Hero */}
      <section ref={heroRef} className="relative h-[70vh] md:h-[85vh] overflow-hidden">
        <motion.img
          src={study.hero}
          srcSet={`${smSrc(study.hero)} 780w, ${study.hero} 1500w`}
          sizes="100vw"
          alt={study.title}
          fetchPriority="high"
          decoding="async"
          style={{ y: imgY, scale: imgScale }}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/60 to-charcoal/45" />
        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">
              {study.category} · {study.year}
            </p>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tight text-cream leading-[0.95]">
              {study.title}
            </h1>
            <p className="mt-5 max-w-2xl text-lg md:text-xl text-cream/80">{study.summary}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {study.tags.map((t) => (
                <span key={t} className="backdrop-blur-md bg-cream/15 text-cream text-sm px-4 py-2 rounded-full border border-cream/20">{t}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Metrics */}
      <section className="bg-charcoal text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {study.metrics.map((m) => (
            <Reveal key={m.label}>
              <div>
                <p className="font-heading text-5xl md:text-6xl text-gold leading-none">{m.value}</p>
                <p className="mt-3 text-sm text-cream/60">{m.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Block label="The Problem">{study.problem}</Block>
        <Block label="Strategy">{study.strategy}</Block>
        <Block label="Creative Process">{study.process}</Block>

        {/* Gallery — real project visuals, shown full-width */}
        <div className="flex flex-col gap-6 md:gap-8 my-16">
          {study.gallery.map((g, i) => (
            <Reveal key={i} delay={0.05}>
              <div className="overflow-hidden rounded-2xl md:rounded-3xl border border-charcoal/5 bg-white shadow-[0_20px_50px_-40px_rgba(34,34,34,0.4)]">
                <img
                  src={g}
                  srcSet={`${smSrc(g)} 780w, ${g} 1500w`}
                  sizes="(max-width: 768px) 100vw, 60vw"
                  alt={`${study.title} visual ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
              </div>
            </Reveal>
          ))}
        </div>

        <Block label="Final Solution">{study.solution}</Block>

        {/* Outcome highlight */}
        <Reveal>
          <div className="mt-12 rounded-3xl bg-burgundy text-cream p-10 md:p-16">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">Business Outcome</p>
            <p className="font-heading text-2xl md:text-4xl leading-snug">{study.outcome}</p>
          </div>
        </Reveal>
      </section>

      {/* Next project */}
      <section className="border-t border-charcoal/10">
        <button
          onClick={() => navigate(`/case-study/${next.id}`)}
          className="group block w-full max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 text-left"
          data-testid="next-case-button"
        >
          <p className="text-xs tracking-[0.25em] uppercase text-charcoal/40 mb-4">Next project</p>
          <div className="flex items-center justify-between gap-6">
            <h2 className="font-heading text-4xl md:text-6xl tracking-tight text-charcoal group-hover:text-burgundy transition-colors">
              {next.title}
            </h2>
            <span className="flex h-14 w-14 md:h-20 md:w-20 items-center justify-center rounded-full border border-charcoal/20 group-hover:bg-burgundy group-hover:border-burgundy group-hover:text-cream transition-colors">
              <ArrowRight size={26} />
            </span>
          </div>
        </button>
      </section>

      <div className="bg-charcoal text-cream py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-heading text-3xl md:text-4xl">Have a project like this?</h3>
          <Link to="/#contact" onClick={() => { setTimeout(() => { const el = document.querySelector('#contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }} className="inline-flex items-center gap-2 rounded-full bg-cream text-charcoal hover:bg-gold px-8 py-4 text-sm font-medium transition-colors" data-testid="case-cta-contact">
            Let's build your brand <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
